import { useCallback, useEffect, useState } from 'react';
import { api, fmtMoney, fmtDate } from '../lib/api';

type Detail = Awaited<ReturnType<typeof api.account>>;

export function AccountDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await api.account(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  async function saveNote() {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.addNote(id, note.trim());
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Note failed');
    } finally {
      setSavingNote(false);
    }
  }

  if (error) return <div><button className="btn quiet" onClick={onBack}>← Back</button><div className="error-banner" style={{ marginTop: 12 }}>{error}</div></div>;
  if (!detail) return <div className="empty">Loading account…</div>;
  const a = detail.account;

  return (
    <div>
      <button className="btn quiet" onClick={onBack}>← ACCOUNT RADAR</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, margin: '16px 0 6px' }}>
        <div className="page-title" style={{ marginBottom: 0 }}>{a.is_demo && '[DEMO] '}{a.display_name}</div>
        <span className={`tier ${a.tier}`}>{a.tier.replace('_', ' ')}</span>
        <span className={`score-num ${a.account_score >= 75 ? 'HOT' : a.account_score >= 60 ? 'HIGH' : 'MEDIUM'}`} style={{ fontSize: 34 }}>
          {a.account_score}
        </span>
      </div>
      {a.recommendation && <div className="why" style={{ margin: '12px 0 20px' }}><b>RECOMMENDATION</b>{a.recommendation}</div>}

      <div className="metrics">
        <div className="metric"><div className="label">Properties</div><div className="value teal">{a.property_count}</div></div>
        <div className="metric"><div className="label">Commercial</div><div className="value">{a.commercial_count}</div></div>
        <div className="metric"><div className="label">Portfolio Value</div><div className="value">{fmtMoney(a.portfolio_value_total)}</div></div>
        <div className="metric"><div className="label">Active Opportunities</div><div className="value hot">{detail.opportunities.filter((o) => ['active', 'saved', 'contacted'].includes(String(o.status))).length}</div></div>
      </div>

      <div className="panel">
        <h3>Business Identity</h3>
        <table>
          <tbody>
            <tr><td>Verified</td><td className="num">{a.is_verified ? 'Yes — Google Places match' : 'Not verified'}</td></tr>
            <tr><td>Phone</td><td className="num">{a.phone ?? '—'}</td></tr>
            <tr><td>Website</td><td className="num">{a.website ? <a href={a.website} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>{a.website}</a> : '—'}</td></tr>
            <tr><td>Business address</td><td className="num">{a.business_address ?? '—'}</td></tr>
            <tr><td>Rating</td><td className="num">{a.rating != null ? `${a.rating} (${a.rating_count ?? 0} reviews)` : '—'}</td></tr>
          </tbody>
        </table>
        <div className="notice">Public parcel ownership does not mean the owner requested contractor contact.</div>
      </div>

      <div className="panel">
        <h3>Portfolio ({a.property_count} properties{detail.portfolio.length < a.property_count ? `, showing ${detail.portfolio.length}` : ''})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr><th>TMS</th><th>Address</th><th>Land Use</th><th>Acres</th><th>Adjusted Total</th></tr>
            </thead>
            <tbody>
              {detail.portfolio.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: 'var(--mono)' }}>{String(p.tms ?? '—')}</td>
                  <td>{String(p.property_address ?? '—')}</td>
                  <td>{String(p.land_use ?? '—')}{p.is_commercial ? ' •' : ''}</td>
                  <td className="num">{p.acres != null ? Number(p.acres).toFixed(1) : '—'}</td>
                  <td className="num">{p.adjusted_total != null ? fmtMoney(Number(p.adjusted_total)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <h3>Opportunity History</h3>
        {detail.opportunities.length === 0 && <div className="notice">No opportunities linked yet.</div>}
        <table>
          <tbody>
            {detail.opportunities.map((o, i) => (
              <tr key={i}>
                <td className="num" style={{ width: 50 }}>{String(o.score)}</td>
                <td>{String(o.title)}</td>
                <td>{String(o.status).toUpperCase()}</td>
                <td>{fmtDate(o.published_at as string | null)}</td>
                <td>{String(o.primary_source_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>Activity Timeline</h3>
        {detail.timeline.length === 0 && <div className="notice">No activity yet.</div>}
        <table>
          <tbody>
            {detail.timeline.map((t, i) => (
              <tr key={i}>
                <td style={{ width: 160 }}>{fmtDate(t.created_at)}</td>
                <td style={{ textTransform: 'uppercase', fontWeight: 600, color: 'var(--text)' }}>{t.action.replace(/_/g, ' ')}</td>
                <td>{t.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>Notes</h3>
        <textarea className="note-input" placeholder="Add a note about this account…"
          value={note} onChange={(e) => setNote(e.target.value)} />
        <div style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={saveNote} disabled={savingNote || !note.trim()}>
            {savingNote ? 'SAVING…' : 'ADD NOTE'}
          </button>
        </div>
        <div style={{ marginTop: 14 }}>
          {detail.notes.map((n) => (
            <div key={n.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
              <div style={{ color: 'var(--text-faint)', fontSize: 12 }}>{fmtDate(n.created_at)}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{n.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
